import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageProps } from "@/types/PageProps";
import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import AdminLayout from "./layout";

type Filter = "invited" | "not_invited" | "no_user_yet";

interface WaitListItem {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  referral?: string;
  created_at: string;
  is_invited: boolean;
  user_id: string;
  user_exists: boolean;
}

type Props = PageProps & {
  wait_list_emails: WaitListItem[];
  total_count: number;
  filters?: Filter[];
};

function FilterLink({
  children,
  active,
  filterKey,
}: {
  children: React.ReactNode;
  active: boolean;
  filterKey: Filter;
}) {
  const urlString = window.location.href;
  const fullUrl = new URL(urlString);
  const currentSearchParams = new URLSearchParams(fullUrl.searchParams);
  const currentFiltersString = currentSearchParams.get("filters") || null;
  const currentFilters = currentFiltersString?.split(",") || [];
  let newFilters;
  if (currentFilters.includes(filterKey)) {
    newFilters = currentFilters.filter((f) => f !== filterKey);
  } else {
    newFilters = [...currentFilters, filterKey];
  }

  currentSearchParams.set("filters", newFilters.join(","));
  const newUrl = new URL(urlString);
  newUrl.searchParams.set("filters", newFilters.join(","));

  return (
    <Link
      href={newUrl.toString()}
      className={buttonVariants({ variant: active ? "secondary" : "outline" })}
    >
      {children}
    </Link>
  );
}

export default function WaitListEmails({ wait_list_emails, total_count, filters }: Props) {
  const [search, setSearch] = useState("");

  const filteredList = wait_list_emails.filter((email) => {
    if (search.length === 0) {
      return true;
    }

    if (email.email.toLowerCase().includes(search.toLowerCase())) {
      return true;
    }

    if (email.first_name?.toLowerCase().includes(search.toLowerCase())) {
      return true;
    }

    if (email.last_name?.toLowerCase().includes(search.toLowerCase())) {
      return true;
    }

    if (email.referral?.toLowerCase().includes(search.toLowerCase())) {
      return true;
    }

    return false;
  });

  return (
    <AdminLayout pageName="waitlist">
      <div>
        <h1 className="text-2xl font-bold">Wait List</h1>
        <p className="text-sm text-gray-500 mb-6">{total_count} people are on the wait list</p>

        <div className="pb-4 space-y-2">
          <p className="text-sm font-medium">Filters</p>
          <div className="flex items-center gap-2">
            <FilterLink active={filters?.includes("invited") ?? false} filterKey="invited">
              Only Invited
            </FilterLink>

            <FilterLink active={filters?.includes("not_invited") ?? false} filterKey="not_invited">
              Not Invited
            </FilterLink>

            <FilterLink active={filters?.includes("no_user_yet") ?? false} filterKey="no_user_yet">
              No User Yet
            </FilterLink>

            <Input
              type="text"
              placeholder="Search..."
              className="max-w-md"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button
              variant="outline"
              className="ml-auto"
              onClick={() => {
                const headers = [
                  "Email",
                  "First Name",
                  "Last Name",
                  "Referral",
                  "Added to list",
                  "Invited",
                ];
                const csvContent = [
                  headers.join(","),
                  ...filteredList.map((item) => {
                    const columns = [
                      item.email,
                      item.first_name,
                      item.last_name,
                      item.referral,
                      item.created_at,
                      item.is_invited ? "Yes" : "No",
                      item.user_exists ? "Yes" : "No",
                      item.user_id,
                    ].map((value) => {
                      // Handle null/undefined values
                      if (value === null || value === undefined) return '""';
                      // Escape quotes and wrap in quotes, also escape newlines
                      return `"${String(value).replace(/"/g, '""').replace(/\n/g, "\\n")}"`;
                    });

                    return columns.join(",");
                  }),
                ].join("\n");

                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "waitlist.csv";
                a.click();
              }}
            >
              Download CSV
            </Button>
          </div>

          {filters?.length && filters.length > 0 ? (
            <div className="text-sm text-muted-foreground">
              Showing {filteredList.length} of {total_count}
            </div>
          ) : null}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invited</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Referral</TableHead>
                <TableHead>Added to list</TableHead>
                <TableHead>User Exists</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredList.map((email) => (
                <TableRow key={email.id}>
                  <TableCell>
                    <Switch
                      checked={email.is_invited}
                      onCheckedChange={async (checked) => {
                        await router.patch(
                          `/admin/wait_list/${email.id}`,
                          {
                            is_invited: checked,
                          },
                          {
                            preserveScroll: true,
                          },
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {email.first_name} {email.last_name}
                  </TableCell>
                  <TableCell>{email.email}</TableCell>
                  <TableCell className="max-w-sm">{email.referral}</TableCell>
                  <TableCell>
                    {new Date(email.created_at).toLocaleString("default", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </TableCell>
                  <TableCell>{email.user_exists ? "Yes" : "No"}</TableCell>
                  <TableCell>{email.user_id}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={async () => {
                        console.log(email.id);
                        await router.delete(`/admin/wait_list/${email.id}`, {
                          preserveScroll: true,
                        });
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
}
